import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export type Persona = "buyer" | "creator" | "seller";
export type PlanKey = "creator" | "starter" | "pro" | "business" | "custom";

// Rank used for min-plan gating. Legacy free/solo map low; unknown → creator
// so shared tools stay visible and only true premium items lock.
export const PLAN_RANK: Record<string, number> = {
  free: 0,
  solo: 1,
  creator: 1,
  starter: 2,
  pro: 3,
  business: 4,
  custom: 5,
};

// Onboarding persona (backend) → product lane (spec §7).
function toPersona(p?: string | null): Persona {
  if (p === "shopper" || p === "individual") return "buyer";
  if (p === "creator") return "creator";
  if (p === "brand") return "seller";
  // Null/legacy accounts (incl. founder & existing workspaces) default to the
  // full seller lane so nothing regresses for current users.
  return "seller";
}

export interface Entitlements {
  persona: Persona;
  planId: string;
  planRank: number;
  isSuperAdmin: boolean;
  loading: boolean;
}

export function useEntitlements(): Entitlements {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;

  const { data, isLoading } = useQuery({
    queryKey: ["entitlement-subscription"],
    queryFn: async () => {
      const { data } = await api.get("/billing/subscription");
      return data as { plan_id?: string };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const planId = (data?.plan_id || "").toLowerCase();
  const planRank = planId in PLAN_RANK ? PLAN_RANK[planId] : 1;

  return {
    persona: toPersona(user?.persona),
    planId,
    planRank,
    isSuperAdmin,
    loading: !!user && isLoading,
  };
}

// ── Route-level persona access (P0 guard) ───────────────────────────────
// Prevents deep-linking into a lane you don't belong to. Persona-only here;
// plan-tier hard-gating is enforced server-side in a later phase. Prefix match.
const SELLER_ONLY_PREFIXES = [
  "/products",
  "/workspace/catalog",
  "/workspace/sell",
  "/workspace/clients",
  "/settings/shopify",
  "/complaints",
  "/workspace/ai/money-meter",
  "/workspace/ai/autopilot",
  "/workspace/marketing/whatsapp",
  "/workspace/marketing/sms",
  "/workspace/marketing/broadcast",
  "/workspace/brand",
];
const CREATOR_ONLY_PREFIXES = ["/workspace/creator"];
// Dashboard routes a buyer is allowed to open (everything else redirects them
// back to the shop, since buyers live on /shop + /myaccount).
const BUYER_ALLOWED_PREFIXES = ["/discovery", "/referrals", "/affiliate", "/workspace/support"];

const startsWithAny = (path: string, prefixes: string[]) =>
  prefixes.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p));

/** Returns a redirect path if the persona may NOT view this dashboard route,
 *  or null if access is allowed. Super Admin always allowed. */
export function routeRedirect(
  pathname: string,
  persona: Persona,
  isSuperAdmin: boolean,
): string | null {
  if (isSuperAdmin) return null;
  const buyerHome = "/shop";
  const workspaceHome = "/dashboard";

  if (startsWithAny(pathname, SELLER_ONLY_PREFIXES)) {
    if (persona === "seller") return null;
    return persona === "buyer" ? buyerHome : workspaceHome;
  }
  if (startsWithAny(pathname, CREATOR_ONLY_PREFIXES)) {
    if (persona === "creator") return null;
    return persona === "buyer" ? buyerHome : workspaceHome;
  }
  // Shared workspace route: creators & sellers OK; buyers only on their allow-list.
  if (persona === "buyer" && !startsWithAny(pathname, BUYER_ALLOWED_PREFIXES)) {
    return buyerHome;
  }
  return null;
}
